import Feature from 'ol/Feature';
import {Point} from 'ol/geom';
import {Circle as CircleStyle, Fill, Icon, Style} from 'ol/style';
import {fromLonLat} from 'ol/proj';
import {getVectorContext} from 'ol/render';
import {hexToRGBA} from "@/assets/js/utils";

/**
 * Volcano animation — an erupting volcano: a mountain icon with a lava fountain
 * and an ash plume.
 *
 * PERFORMANCE: the lava/ash particles are NOT OpenLayers features. They are plain
 * objects drawn straight onto the layer canvas in the layer's 'postrender' event
 * (immediate rendering). The previous version spawned and mutated dozens of
 * ol/Feature objects every 40ms — a `new Point()` + `new Style({image: new
 * CircleStyle()})` per particle per frame, plus constant addFeature/removeFeature —
 * which flooded the vector source with `change` events and re-rendered the whole map
 * non-stop, starving the news markers so they stopped repainting on zoom/pan. Drawing
 * in postrender touches the source zero times per frame and allocates nothing (reused
 * geometry + cached styles), so the map's render loop stays free.
 *
 * Expected `data`: { "center": [lon, lat] }
 */
export function volcano(layer, news, data) {
    const center = fromLonLat(data.center);

    const parent = new Feature({geometry: new Point(center), where: center});
    parent.setStyle(new Style({
        image: new Icon({
            src: '/images/volcano.svg',
            scale: 0.5,
            anchor: [0.5, 0.34],
            anchorXUnits: 'fraction',
            anchorYUnits: 'fraction'
        })
    }));
    layer.getSource().addFeature(parent);

    const STEP = 40;
    const MAX_LAVA = 60;
    const MAX_SMOKE = 30;
    const lava = [];
    const smoke = [];

    const pt = new Point(center);
    const styleCache = {};
    function circleStyle(key, radius, color, alpha) {
        let s = styleCache[key];
        if (!s) {
            s = styleCache[key] = new Style({
                image: new CircleStyle({radius, fill: new Fill({color: hexToRGBA(color, alpha)})})
            });
        }
        return s;
    }
    function lavaStyle(p, size) {
        const color = p < 0.35 ? '#fff0a0' : p < 0.7 ? '#ff8c1f' : '#e8430f';
        const alpha = Math.round((p > 0.8 ? (1 - p) / 0.2 : 1) * 10) / 10;
        const radius = Math.round(Math.max(0.5, size * (1 - 0.35 * p)) * 2) / 2;
        return circleStyle('l' + color + radius + '_' + alpha, radius, color, alpha);
    }
    function smokeStyle(p) {
        const alpha = Math.round((1 - p) * 0.5 * 20) / 20;
        const radius = Math.round(4 + p * 16);
        return circleStyle('s' + radius + '_' + alpha, radius, '#5b5650', alpha);
    }

    function spawnLava() {
        if (lava.length >= MAX_LAVA) return;
        lava.push({
            p: 0,
            peak: 1800 + Math.random() * 2800,
            drift: (Math.random() - 0.5) * 3000,
            size: 2 + Math.random() * 2.5,
            speed: 0.03 + Math.random() * 0.02
        });
    }
    function spawnSmoke() {
        if (smoke.length >= MAX_SMOKE) return;
        smoke.push({p: 0, drift: (Math.random() - 0.5) * 1400, speed: 0.012 + Math.random() * 0.01});
    }

    function advance() {
        for (let i = lava.length - 1; i >= 0; i--) {
            if ((lava[i].p += lava[i].speed) >= 1) lava.splice(i, 1);
        }
        for (let i = smoke.length - 1; i >= 0; i--) {
            if ((smoke[i].p += smoke[i].speed) >= 1) smoke.splice(i, 1);
        }
    }

    layer.on('postrender', (event) => {
        if (!lava.length && !smoke.length) return;
        const vctx = getVectorContext(event);
        for (const f of smoke) {
            pt.setCoordinates([center[0] + f.drift * f.p, center[1] + 4500 * f.p]);
            vctx.setStyle(smokeStyle(f.p));
            vctx.drawGeometry(pt);
        }
        for (const f of lava) {
            pt.setCoordinates([center[0] + f.drift * f.p, center[1] + f.peak * Math.sin(f.p * Math.PI)]);
            vctx.setStyle(lavaStyle(f.p, f.size));
            vctx.drawGeometry(pt);
        }
    });

    let last = performance.now();
    let simAcc = 0, lavaAcc = 0, smokeAcc = 0;
    function frame(now) {
        let dt = now - last;
        last = now;
        if (dt > 250) dt = 250; // drop the big gap after the tab was backgrounded
        simAcc += dt; lavaAcc += dt; smokeAcc += dt;
        let stepped = false;
        while (simAcc >= STEP) { advance(); simAcc -= STEP; stepped = true; }
        while (lavaAcc >= 110) { const n = 2 + Math.floor(Math.random() * 3); for (let i = 0; i < n; i++) spawnLava(); lavaAcc -= 110; }
        while (smokeAcc >= 240) { spawnSmoke(); smokeAcc -= 240; }
        if (stepped && (lava.length || smoke.length)) layer.changed();
        requestAnimationFrame(frame);
    }
    requestAnimationFrame(frame);

    return {
        getFeature() {
            return parent;
        }
    };
}
